// --- API 공통 ---
export interface ApiResponse<T> {
  code: number;
  message: string;
  result: T;
}

// --- Enum ---
export const BusinessRegistrationTypes = {
  SOLE: "SOLE",
  CORP: "CORP",
} as const;
export type BusinessRegistrationType =
  (typeof BusinessRegistrationTypes)[keyof typeof BusinessRegistrationTypes];

export const BusinessStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  SUSPENDED: "SUSPENDED",
  PENDING: "PENDING",
  CLOSED: "CLOSED",
} as const;
export type BusinessStatusType =
  (typeof BusinessStatus)[keyof typeof BusinessStatus];

export const BUSINESS_STATUS_MAP: Record<
  BusinessStatusType,
  { label: string; color: string }
> = {
  ACTIVE: { label: "활성", color: "bg-green-100 text-green-700" },
  INACTIVE: { label: "비활성", color: "bg-slate-100 text-slate-700" },
  SUSPENDED: { label: "정지", color: "bg-red-100 text-red-700" },
  PENDING: { label: "승인대기", color: "bg-yellow-100 text-yellow-700" },
  CLOSED: { label: "폐업", color: "bg-gray-100 text-gray-500" },
};

// --- 배차 업체 리스트 (Search-Dispatch) ---
export interface DispatchCompanyListItem {
  companyGuid: string;
  companyName: string;
  companyCode: number | null;
  representativeName: string | null;
  businessRegNum: string | null;
  phoneNum: string | null;
  isActive: boolean;
  isPause: boolean;
  businessStatus: BusinessStatusType;
  registDate: string | null;
}

export interface CompanySearchRequest {
  page: number;
  pageSize: number;
  searchStr: string;
  isActive: boolean | null;
}

export interface CompanySearchResult {
  page: number;
  totalCount: number;
  totalPages: number;
  dispatchCompanies: DispatchCompanyListItem[];
}

// --- 배차 업체 상세 (Get-Dispatch-Company) ---
export interface DispatchCompanyDetail extends DispatchCompanyListItem {
  email: string | null;
  businessType: string | null;
  businessItem: string | null;
  businessRegistrationType: BusinessRegistrationType | null;
  addressHq: string | null;
  managerName: string | null;
  managerPhoneNum: string | null;
  phoneNumSub: string | null;
  creditLimit: number | null;
  departmentInCharge: string | null;
  faxNum: string | null;
  serviceRange: string[];
  updateDate: string | null;
  // 부가정보 (ul_company_dispatch)
  smsBrandName: string | null;
  smsSenderNumber: string | null;
  postalZipCode: string | null;
  postalAddress: string | null;
  postalRecipient: string | null;
  isAutoCompleteStateEnabled: boolean;
  isCompletedEditGuardEnabled: boolean;
  /** 공통(기본 셋팅) 단가 폴백 사용 여부. 다른 플래그와 달리 기본값이 true(사용)다 — 옵트아웃 */
  useCommonPriceFallback: boolean;
  /** 화물리스트 기본 정렬 프리셋 key (LOAD_TIME/DROP_TIME/RECENT_DISPATCH/REGIST). null=LOAD_TIME(상차시간순) */
  defaultCargoSort: string | null;
  /** SNS 화물 관리 사용 여부. 채팅 수집기(CaptainHook)를 돌리는 업체만 켠다 — 옵트인(기본 false) */
  isSnsCargoEnabled: boolean;
  /** 화물 리스트 「차주 리스트」 탭 사용 여부 — 옵트인(기본 false) */
  isDriverListEnabled: boolean;
  /**
   * 신규 화물 기본 결제타입. 화주에 기본 결제타입이 없을 때 쓰는 폴백.
   * 서버가 미설정 업체도 시스템 기본("신용")을 채워 내린다.
   */
  defaultPayMethod: string | null;
  /**
   * 화물 비고1 표시 라벨(화물 목록 헤더·상세 "기타 정보" 탭).
   * 서버가 미설정 업체도 기본 라벨("비고1")을 채워 내린다.
   */
  cargoRemark1Label: string | null;
  /** 화물 비고2 표시 라벨. cargoRemark1Label 과 동일 */
  cargoRemark2Label: string | null;
  /**
   * 화물 비고 노출 개수(0~2). 0 = 비고 미사용(기본) — ULA 화물 목록 컬럼·상세 "기타 정보" 탭이 통째로 숨는다.
   * 라벨을 정해 두어도 이 값이 0이면 아무것도 보이지 않는다.
   */
  cargoRemarkCount: number;
}

// --- 배차 업체 추가 (Add-Dispatch-Company) ---
export interface AddDispatchCompanyRequest {
  companyName: string;
  representativeName?: string;
  businessRegNum?: string;
  businessRegistrationType?: BusinessRegistrationType;
  businessType?: string;
  businessItem?: string;
  phoneNum?: string;
  phoneNumSub?: string;
  email?: string;
  addressHq?: string;
  managerName?: string;
  managerPhoneNum?: string;
  creditLimit?: number;
  departmentInCharge?: string;
  faxNum?: string;
}

// --- 배차 업체 수정 (Update-Dispatch-Company) ---
export interface UpdateDispatchCompanyRequest {
  companyGuid: string;
  companyName?: string;
  representativeName?: string;
  businessRegNum?: string;
  businessRegistrationType?: BusinessRegistrationType;
  businessType?: string;
  businessItem?: string;
  phoneNum?: string;
  phoneNumSub?: string;
  email?: string;
  addressHq?: string;
  managerName?: string;
  managerPhoneNum?: string;
  creditLimit?: number;
  departmentInCharge?: string;
  faxNum?: string;
  isUsingDispatchService?: boolean;
  isActive?: boolean;
  isPause?: boolean;
  businessStatus?: BusinessStatusType;
}

// --- 배차 업체 부가정보 Upsert (Upsert-Dispatch-Settings) ---
export interface UpsertDispatchSettingsRequest {
  CompanyGuid: string;
  SmsBrandName: string | null;
  SmsSenderNumber: string | null;
  PostalZipCode: string | null;
  PostalAddress: string | null;
  PostalRecipient: string | null;
  IsAutoCompleteStateEnabled: boolean;
  IsCompletedEditGuardEnabled: boolean;
  /**
   * 공통(기본 셋팅) 단가 폴백 사용 여부.
   * 서버는 미전송(null)이면 기존 설정을 보존한다 — 값으로 접지 않으므로 항상 실제 값을 보낸다.
   */
  UseCommonPriceFallback: boolean;
  /** 화물리스트 기본 정렬 프리셋 key (LOAD_TIME/DROP_TIME/RECENT_DISPATCH/REGIST). 빈 문자열이면 서버가 기본으로 리셋 */
  DefaultCargoSort: string | null;
  /**
   * SNS 화물 관리 사용 여부.
   * 서버는 미전송(null)이면 기존 설정을 보존한다 — 값으로 접지 않으므로 항상 실제 값을 보낸다.
   */
  IsSnsCargoEnabled: boolean;
  /** 화물 리스트 「차주 리스트」 탭 사용 여부. IsSnsCargoEnabled 와 같은 규칙 — 항상 실제 값을 보낸다 */
  IsDriverListEnabled: boolean;
  /**
   * 신규 화물 기본 결제타입. ul_sys_item(PAYMENT_METHOD) 항목명이어야 하며, 목록 밖의 값은 서버가 거부한다.
   * 빈 문자열이면 서버가 미설정(시스템 기본 "신용")으로 리셋.
   */
  DefaultPayMethod: string | null;
  /**
   * 화물 비고1 표시 라벨(최대 32자). 빈 문자열이면 서버가 기본 라벨("비고1")로 리셋.
   * 값(비고 내용)은 화물마다 다르고, 여기서 정하는 건 그 값을 뭐라고 부를지다.
   */
  CargoRemark1Label: string | null;
  /** 화물 비고2 표시 라벨. CargoRemark1Label 과 동일 */
  CargoRemark2Label: string | null;
  /**
   * 화물 비고 노출 개수(0~2). 0이면 ULA 에서 비고 컬럼·탭이 통째로 숨는다.
   * 미전송(null)이면 서버가 기존 설정을 보존한다. 범위 밖 값은 서버가 0~2로 clamp.
   */
  CargoRemarkCount: number | null;
}

// --- 게스트 SMS 시스템 템플릿 ---
export interface SystemTemplateItem {
  title: string;
  template: string;
  templateGuid: string;
  registDate: string | null;
}

export interface CompanySystemTemplate {
  /** 업체 전용 override (있으면 적용 중) */
  override: SystemTemplateItem | null;
  /** 타입별 시스템 기본 */
  systemDefault: SystemTemplateItem | null;
}

export interface UpsertCompanySystemTemplateRequest {
  CompanyGuid: string;
  TemplateType: string;
  Title: string;
  Template: string;
}

// --- 화주 업체 검색 (Search-Cargo-Company-Names) ---
export interface CargoCompanyNameItem {
  companyGuid: string;
  companyName: string;
  regSource: string | null;
  regSourceId: string | null;
}

// --- 화주 업체 상세 (Get-Cargo-Company) ---
export interface CargoCompanyDetail {
  companyGuid: string;
  companyName: string;
  companyCode: string | null;
  representativeName: string | null;
  businessRegNum: string | null;
  businessRegistrationType: BusinessRegistrationType | null;
  businessType: string | null;
  businessItem: string | null;
  phoneNum: string | null;
  phoneNumSub: string | null;
  email: string | null;
  addressHq: string | null;
  managerName: string | null;
  managerPhoneNum: string | null;
  departmentInCharge: string | null;
  faxNum: string | null;
  registDate: string | null;
  updateDate: string | null;
}
